import toast from 'react-hot-toast';
import { ActionFunction, LoaderFunction, redirect } from 'react-router-dom';

import { AxiosRequestConfig } from 'axios';

import { AuthenticatedApiService } from '@/services/api/BaseApiService';
import { handleActionErrors } from '@/services/api/errors';

import { Clinic } from './types';

export interface GetAllClinicsResponse {
  clinics: Clinic[];
}

class ClinicApiService extends AuthenticatedApiService {
  getClinics = (
    onlyWithDirectPatients?: boolean,
    onlyWithReferrals?: boolean,
    config?: AxiosRequestConfig
  ) =>
    this.get<GetAllClinicsResponse>('/clinics', {
      params: { onlyWithDirectPatients, onlyWithReferrals },
      ...config,
    });

  getClinic = (id: string, config?: AxiosRequestConfig) =>
    this.get<Clinic>(`/clinics/${id}`, config);

  createClinic = (data: Omit<Clinic, 'id'>, config?: AxiosRequestConfig) =>
    this.post<{ clinicId: Guid }>('/clinics', data, config);

  updateClinic = (data: Omit<Clinic, 'countryCode' | 'usStateCode'>, config?: AxiosRequestConfig) =>
    this.put<Clinic>(`/clinics/${data.id}`, data, config);

  loader: LoaderFunction = async ({ request }) => {
    return {
      clinics: this.getClinics(undefined, undefined, {
        signal: request.signal,
      }).promise,
    };
  };

  clinicLoader: LoaderFunction = async ({ params }) => {
    if (!params.clinicId) {
      throw new Error('Clinic id was not found.');
    }

    return {
      clinic: await this.getClinic(params.clinicId).promise,
    };
  };

  createClinicAction: ActionFunction = async ({ request }) => {
    try {
      const data = await request.json();
      await this.createClinic(data).promise;
      toast.success('Clinic added');

      return redirect('/admin/clinics/search');
    } catch (error) {
      return handleActionErrors<Clinic>(error);
    }
  };

  updateClinicAction: ActionFunction = async ({ params, request }) => {
    const clinicId = params.clinicId;

    if (!clinicId) {
      throw new Error('Clinic id was not found.');
    }

    try {
      const data = await request.json();
      await this.updateClinic({ ...data, id: clinicId }).promise;
      toast.success('Clinic updated');

      return redirect('/admin/clinics/search');
    } catch (error) {
      return handleActionErrors<Clinic>(error);
    }
  };
}

export const clinicApiService = new ClinicApiService();
